/**
 * CheckboxGroup, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/CheckboxGroupComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { labelLengthWarnings } from '../labelLength';
import { reservedFieldNameErrors } from '../fieldName';
import { DATA_SOURCE_MODE, DATA_SOURCE_REF, defaultBindingProps, isDynamic, dataSourceForWhatsApp, readLoadedDataSource, validateBinding } from '../dataSourceBinding';

export const CheckboxGroupCore = {
  type: 'CheckboxGroup',
  name: 'Checkbox Group',
  icon: '☑️',
  description: 'Pick one or more options from a visible list.',
  category: 'selection',
  maxPerScreen: null,
  getDefaultProperties() {
    return {
      name: 'checkbox_group',
      label: 'Choose all that apply',
      required: false,
      'data-source': [
        { id: 'option1', title: 'Option 1' },
        { id: 'option2', title: 'Option 2' },
      ],
      ...defaultBindingProps(),
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      'data-source': {
        type: 'array',
        label: 'Options',
        required: true,
        itemSchema: { id: 'string', title: 'string', metadata: 'string' },
      },
    };
  },
  validateProperties(properties) {
    const errors = [];
    const warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    warnings.push(...labelLengthWarnings('CheckboxGroup', properties.label));

    if (isDynamic(properties)) {
      const binding = validateBinding(properties);
      errors.push(...binding.errors);
      warnings.push(...binding.warnings);
      return { errors, warnings };
    }

    if (!properties['data-source'] || properties['data-source'].length === 0) {
      errors.push('At least one option is required');
    }

    // Validate options
    if (properties['data-source']) {
      const ids = new Set();
      properties['data-source'].forEach((option, index) => {
        if (!option.id) {
          errors.push(`Option ${index + 1} is missing an ID`);
        } else if (ids.has(option.id)) {
          errors.push(`Duplicate option ID: ${option.id}`);
        } else {
          ids.add(option.id);
        }

        if (!option.title) {
          errors.push(`Option ${index + 1} is missing a title`);
        }
      });

      if (properties['data-source'].length > 20) {
        errors.push('Maximum 20 options allowed');
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    return {
      type: 'CheckboxGroup',
      name: properties.name,
      label: properties.label,
      required: properties.required || false,
      'data-source': dataSourceForWhatsApp(properties, properties['data-source'] || []),
    };
  },
  transformFromWhatsApp(whatsappComponent) {
    const loaded = readLoadedDataSource(whatsappComponent['data-source']);
    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      required: whatsappComponent.required || false,
      'data-source': loaded.array,
      [DATA_SOURCE_MODE]: loaded.mode,
      [DATA_SOURCE_REF]: loaded.ref,
    };
  },
};
